import {Box} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {AppsLine} from '@dropbox/dig-icons/assets';
import {logDcdOwnershipDetailInteraction} from 'analytics/dcdOwnershipAnalytics';
import cx from 'classnames';
import type {DcdOwnershipService} from 'client';
import {SearchEmployee, useSearchesWithStatus} from 'hooks/useEmployee';
import {t} from 'i18next';

import {DcdOwnershipAppApprovalsCard} from './DcdOwnershipAppApprovalsCard';
import styles from './DcdOwnershipAppDetail.module.css';
import {OwnershipDetailLayout} from './OwnershipDetailLayout';
import {useDcdOwnershipPageType} from './ownershipHelpers';
import {TeamMembersCard, TeamMemberSummary} from './TeamMembersCard';

const normalizeName = (name: string) => name.trim().replace(/\s+/g, ' ').toLocaleLowerCase();

const splitOwnerNames = (names?: string | null) =>
  (names ?? '')
    .split(',')
    .map((name) => name.trim())
    .filter(Boolean);

const namesMatch = (left: string, right: string) =>
  normalizeName(left).localeCompare(normalizeName(right), undefined, {
    sensitivity: 'base',
  }) === 0;

const uniqueEmployees = (employees: SearchEmployee[]) =>
  new Map(employees.map((employee) => [employee.user_id, employee]));

const findOwner = (ownerName: string, ownerMatches: SearchEmployee[]) => {
  const exactMatches = uniqueEmployees(
    ownerMatches.filter(({name}) => namesMatch(name, ownerName))
  );
  if (exactMatches.size > 0) {
    return exactMatches.size === 1 ? Array.from(exactMatches.values())[0] : undefined;
  }

  const normalizedOwnerName = normalizeName(ownerName);
  const substringMatches = uniqueEmployees(
    ownerMatches.filter(({name}) => normalizeName(name).includes(normalizedOwnerName))
  );
  return substringMatches.size === 1 ? Array.from(substringMatches.values())[0] : undefined;
};

const ownerRoleRank = ({
  isBusinessOwner,
  isSystemOwner,
}: {
  isBusinessOwner: boolean;
  isSystemOwner: boolean;
}) => {
  if (isBusinessOwner && isSystemOwner) {
    return 0;
  }
  return isBusinessOwner ? 1 : 2;
};

export const DcdOwnershipAppDetail = ({app}: {app: DcdOwnershipService}) => {
  const pageType = useDcdOwnershipPageType();
  const owners = [
    ...splitOwnerNames(app.owner_name).map((name) => ({
      name,
      isBusinessOwner: true,
      isSystemOwner: false,
    })),
    ...splitOwnerNames(app.system_owner_name).map((name) => ({
      name,
      isBusinessOwner: false,
      isSystemOwner: true,
    })),
  ];
  const {results: ownerMatches, isLoading: isOwnerLoading} = useSearchesWithStatus({
    ldaps: owners.map(({name}) => name),
  });
  const resolvedOwners = owners.map(({name, isBusinessOwner, isSystemOwner}) => {
    const employee = isOwnerLoading ? undefined : findOwner(name, ownerMatches);
    return {employee, name, isBusinessOwner, isSystemOwner};
  });
  const uniqueOwners = Array.from(
    resolvedOwners
      .reduce((ownersById, owner) => {
        const key = owner.employee?.user_id ?? `unresolved:${normalizeName(owner.name)}`;
        const existing = ownersById.get(key);
        ownersById.set(key, {
          ...owner,
          isBusinessOwner: owner.isBusinessOwner || existing?.isBusinessOwner === true,
          isSystemOwner: owner.isSystemOwner || existing?.isSystemOwner === true,
        });
        return ownersById;
      }, new Map<string, (typeof resolvedOwners)[number]>())
      .values()
  ).sort((left, right) => ownerRoleRank(left) - ownerRoleRank(right));
  const ownerMembers: TeamMemberSummary[] = uniqueOwners.map(
    ({employee, name, isBusinessOwner, isSystemOwner}) => {
      const resolvedName = employee?.name ?? name;
      const ownershipRole =
        isBusinessOwner && isSystemOwner
          ? t('business_system_owner_role')
          : t(isSystemOwner ? 'system_owner_role' : 'business_owner_role');

      return {
        user_id: employee?.user_id ?? `unresolved:${normalizeName(name)}`,
        ldap: employee?.ldap ?? '',
        name: resolvedName,
        role: employee?.role,
        additionalProfileInfo: ownershipRole,
        viewOnly: employee === undefined,
      };
    }
  );

  return (
    <OwnershipDetailLayout
      title={app.name}
      description={app.description}
      titleAccessory={<UIIcon src={AppsLine} aria-label="App" />}
    >
      <Box
        className={cx(styles.columns, {
          [styles.columnsWithoutOwners]: ownerMembers.length === 0,
        })}
      >
        {ownerMembers.length > 0 ? (
          <TeamMembersCard
            members={ownerMembers}
            useSingleColumn
            title={t(ownerMembers.length === 1 ? 'owner' : 'owners')}
            source="dcd_ownership_app_detail"
            onClick={
              pageType
                ? () => logDcdOwnershipDetailInteraction('owner_clicked', {pageType})
                : undefined
            }
          />
        ) : null}
        <DcdOwnershipAppApprovalsCard
          approvals={app.approvals}
          dataClassificationLevel={app.data_classification_level}
        />
      </Box>
    </OwnershipDetailLayout>
  );
};
